import type { FormField } from "@tellescope/types-models";
import { subFieldIdsOf } from "../../utils";
import type { FormFieldNode } from "../../utils/types";
import type { FieldStack } from "./field-stack";

/** A stacked field with what validation says about its current answer. */
export interface FieldStackEntry {
  node: FormFieldNode;
  error: string | null;
  subFieldErrors: Record<string, string | null>;
}

/**
 * A validation error on a field that would be submitted, and so is holding the Submit button
 * disabled. One entry per page: a Question Group's sub-field errors are reported against the group,
 * which is the page the enduser can return to through `goToField`.
 */
export interface BlockingFieldError {
  fieldId: string;
  title: string;
  error: string;
}

export const resolveFieldStackEntries = (
  stack: FieldStack,
  validate: (fieldId: string) => string | null
): FieldStackEntry[] =>
  stack.map((node) => ({
    node,
    error: validate(node.value.id),
    subFieldErrors: subFieldIdsOf(node.value).reduce<Record<string, string | null>>(
      (acc, subFieldId) => ({ ...acc, [subFieldId]: validate(subFieldId) }),
      {}
    ),
  }));

export const blockingErrorsOf = (
  entries: FieldStackEntry[],
  fieldsMap: Record<string, FormField | undefined>
): BlockingFieldError[] =>
  entries.reduce<BlockingFieldError[]>((acc, { node, error }) => {
    if (!error) return acc;
    return [...acc, { fieldId: node.value.id, title: fieldsMap[node.value.id]?.title ?? "", error }];
  }, []);
